/**
 * Sprachprinzip Flex4Success v8 (verbindlich):
 * 1. Alltagssprache, keine Anglizismen-Übersetzungen
 * 2. Metaphern aus Andreas' Coaching-Modell (Sanduhr, Radar, Tür/Schlüssel, Standortbestimmung)
 * 3. Psychologisch fundiert, nicht esoterisch
 * 4. Kein Coach-Sprech
 * 5. Werkzeug-Namen in einem Satz erklärbar
 * 6. 'Primärfokustyp' statt 'Fokustyp' im Singular-Kontext
 *
 * Sprint M5.39 (28.07.2026) — FlexCoachAvatar
 *
 * Zentrale Avatar-Komponente „FlexCoach" (Eule im menschlichen Stil, runde
 * Brille, Buch unterm Arm, Anzug). Tier-Logik entscheidet, welche PNG-Datei
 * geladen wird — die finale Illustration wird extern geliefert und unter
 * `/avatars/flex-coach-{tier}.png` abgelegt.
 *
 * Tiers (Sprint M5.39 Phase 2):
 *   - free    → FlexCoach (Schnupper-Plan) — einfache runde Brille
 *   - pro     → FlexCoach Pro (Monatsplan / Jahresplan) — Anzug-Pin
 *   - master  → FlexCoach Master (PowerPaket) — Klemmbrett
 *
 * Konventionen (verbindlich):
 *   - CSS-Klasse: .flex-coach-avatar
 *   - Component:   <FlexCoachAvatar tier="pro" />
 *   - URL-Slug:    /flex-coach
 *   - Token:       --flex-coach-primary
 */
import React from "react";

const TIER_META = {
  free: {
    label: "FlexCoach",
    accessory: "Schnupper-Plan · einfache runde Brille",
    src: "/avatars/flex-coach-free.png",
  },
  pro: {
    label: "FlexCoach Pro",
    accessory: "Monats- oder Jahresplan · Anzug-Pin",
    src: "/avatars/flex-coach-pro.png",
  },
  master: {
    label: "FlexCoach Master",
    accessory: "PowerPaket · Klemmbrett",
    src: "/avatars/flex-coach-master.png",
  },
};

const SIZE_TO_PX = {
  sm: 48,
  md: 96,
  lg: 160,
  xl: 240,
};

export default function FlexCoachAvatar({
  tier = "free",
  size = "md",
  showCaption = false,
  className = "",
}) {
  const meta = TIER_META[tier] || TIER_META.free;
  const px = SIZE_TO_PX[size] || SIZE_TO_PX.md;

  return (
    <figure
      className={`flex-coach-avatar inline-flex flex-col items-center ${className}`.trim()}
      data-testid={`flex-coach-avatar-${tier}`}
      data-tier={tier}
      data-size={size}
      style={{ "--flex-coach-primary": "#4563BB" }}
    >
      <img
        src={meta.src}
        alt={`${meta.label} — Avatar`}
        width={px}
        height={px}
        loading="lazy"
        draggable={false}
        className="rounded-2xl object-contain bg-white ring-2"
        style={{
          width: px,
          height: px,
          "--tw-ring-color": "var(--flex-coach-primary)",
          boxShadow: "0 6px 24px -8px rgba(69, 99, 187, 0.35)",
        }}
        data-testid={`flex-coach-avatar-img-${tier}`}
      />
      {showCaption && (
        <figcaption
          className="mt-3 text-center"
          data-testid={`flex-coach-avatar-caption-${tier}`}
        >
          <p className="text-sm font-bold text-slate-900">{meta.label}</p>
          <p className="text-xs text-slate-500 mt-0.5">{meta.accessory}</p>
        </figcaption>
      )}
    </figure>
  );
}

export { TIER_META as FLEX_COACH_TIERS };
